import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import TableItem from './table-item';
import TableCategoryOnly from './table-category-only';
import { makeStyles } from "@material-ui/core/styles";
import { ListItem, Box } from "@material-ui/core";
import { useDataProvider, useTranslate } from 'react-admin';
import TableSelectedHeader from './table-selected-header';
import SortableHeader from '../common/header-sortable';
import Loading from '../common/loading';
import TableContentNoData from '../common/table-content-no-data';
import SystemFolderPopover from './system-folder-popover';
import { usePopupState, bindToggle } from 'material-ui-popup-state/hooks';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
    root: {
        color: "#88898c",
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'row',
        paddingLeft: 0,
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginTop: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
    },
    rootSelected: {
        backgroundColor: '#237fc2',
        color: theme.palette.common.white,
        '&&:hover': {
            backgroundColor: '#237fc2',
        },
        paddingLeft: 0
    },
    childItem: {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: 13
    },
    name: {
        fontSize: 13,
        cursor: 'pointer',
        '&&:hover': {
            textDecoration: 'underline'
        }
    },
    subtitle: {
        fontSize: 11
    },
    iconContainer: {
        minWidth: 'unset',
        backgroundColor: '#e5f1f8',
        position: 'absolute',
        top: 26,
        right: 16
    },
    icon: {
        width: 16,
        height: 16
    },
    iconMenuContainer: {
        minWidth: 'unset',
        backgroundColor: '#ffffff',
        position: 'absolute',
        top: 26,
        right: 20,
        width: 25,
        height: 25,
        borderRadius: 10,
        border: 'solid 2px #e5f1f8',
    },
    iconMenuContainerSelect: {
        minWidth: 'unset',
        backgroundColor: '#237fc2',
        position: 'absolute',
        top: 26,
        right: 20,
        width: 25,
        height: 25,
        borderRadius: 10,
        border: 'solid 2px #e5f1f8',
        '&&:hover': {
            backgroundColor: '#e5f1f8',
        },
    },
    iconMenu: {
        width: 14,
        height: 14,
    },
    item: {
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 12
    },
    image: {
        marginLeft: 10,
        marginRight: 42,
        paddingTop: 11,
        paddingBottom: 13,
    },
    title: {
        paddingTop: 22,
        paddingBottom: 17
    },
    body: {
        color: 'black'
    }
}));

function TableHeader({ classes, show, onSortable }) {
    const [order, setOrder] = useState({ field: '', order: 'ASC' });
    const translate = useTranslate();

    const onSortClick = (nextSort) => {
        setOrder(nextSort);
        onSortable(nextSort);
    }

    if (show) {
        return (
            <ListItem className={classes.header}>
                <Box flex={0.5} />
                <Box flex={7}>
                    <SortableHeader title={translate('pos.dashboard.name')} field='name' order={order} setOrder={onSortClick} />
                </Box>
                <Box flex={2}>
                    <SortableHeader title={translate('pos.messages.type')} field='type' order={order} setOrder={onSortClick} />
                </Box>
                <Box flex={2}>
                    <SortableHeader title={translate('pos.common.size')} field='size' order={order} setOrder={onSortClick} />
                </Box>
                <Box flex={2}>
                    <SortableHeader title={translate('resources.messages.created')} field='createdAt' order={order} setOrder={onSortClick} />
                </Box>
                <Box flex={1} />
            </ListItem>
        );
    }
    return null;
}

function Table(props) {
    const {
        categories, getCategory, onCheck, onDelete, onDownload, onClear, searchEmpty,
        currentId, setSnackbarMessage, level, onSortable,
        loading, pickerMode, permissions
    } = props;
    const dataProvider = useDataProvider();
    const classes = useStyles();
    const translate = useTranslate();
    const popupStateSysOpen = usePopupState({ variant: 'popper', popupId: 'sysOpen' })
    const { itemSelectedPopup, selectedItems } = useSelector(state => state.file_manager);

    // STATE
    const [popoverState, setPopoverState] = useState('COPY');
    // END STATE

    const openSysPopover = (event, type) => {
        setPopoverState(type);
        bindToggle(popupStateSysOpen).onClick(event);
    }

    const onMoveOrCopy = (toParentId, name, items) => {
        popupStateSysOpen.close();

        let requestUrl, snackbarMsg, totalStr;
        const totalFiles = items.length;
        if (popoverState === 'COPY') {
            requestUrl = 'category/copy';
            snackbarMsg = translate(`pos.categories.actionSuccess`).replace('$NAME', name);
            totalStr = totalFiles === 1 ? translate(`pos.categories.copied`) : `${totalFiles}${translate(`pos.categories.copies`)}`;
        } else {
            requestUrl = 'category/move';
            snackbarMsg = translate(`pos.categories.actionSuccess`).replace('$NAME', name);
            totalStr = totalFiles === 1 ? translate(`pos.categories.moved`) : `${totalFiles}${translate(`pos.categories.moves`)}`;
        }

        let counter = 0;
        const callback = function () {
            counter++;
            if (counter === totalFiles) {
                getCategory(currentId);
                setSnackbarMessage({
                    message: snackbarMsg,
                    boldText: totalStr
                });

                // Close snackbar
                setTimeout(() => {
                    setSnackbarMessage({ message: '', boldText: '' });
                }, 6000);
            }
        }

        items.forEach(item => {
            dataProvider('UPDATE', requestUrl, {
                id: item.id,
                data: { parentId: toParentId }
            }).then(({ data }) => {
                callback();
            }).catch(error => {
                callback();
                console.log('onMoveOrCopy error:', error);
            });
        });
    }

    const handleCopyOrSave = (parentId, name) => {
        if (itemSelectedPopup) {
            onMoveOrCopy(parentId, name, [itemSelectedPopup]);
        } else {
            onMoveOrCopy(parentId, name, selectedItems);
        }
    }

    return (
        <Fragment>
            <TableSelectedHeader
                onDelete={onDelete}
                onDownload={onDownload}
                onClear={onClear}
                openSysPopover={openSysPopover}
            />
            <TableHeader show={selectedItems.length === 0 && level === -1} classes={classes} onSortable={onSortable} />
            <Loading show={loading} />
            {!loading &&
                <Box>
                    <TableContentNoData show={categories.length === 0 && !searchEmpty}>{translate('pos.categories.nodata')}</TableContentNoData>
                    <TableContentNoData show={categories.length === 0 && searchEmpty}>{translate('pos.categories.searchnodata')}</TableContentNoData>
                    {
                        level === -1 ? categories.map(item =>
                            <TableItem
                                permissions={permissions}
                                pickerMode={pickerMode}
                                getCategory={getCategory}
                                item={item}
                                classes={classes}
                                key={item.id + ''}
                                setSnackbarMessage={setSnackbarMessage}
                                onCheck={onCheck}
                                level={level}
                                onDelete={onDelete}
                                popupStateSysOpen={popupStateSysOpen} // Dialog to display content
                                setOpenSysPopupState={setPopoverState} // To change UPDATE OR MOVE
                            />

                        ) : (
                                categories.map(item =>
                                    <TableCategoryOnly
                                        key={item.id + ''}
                                        getCategory={getCategory}
                                        item={item}
                                        classes={classes}
                                        onCheck={onCheck}
                                        permissions={permissions}
                                        pickerMode={pickerMode}
                                    />
                                )
                            )
                    }
                </Box>
            }
            <SystemFolderPopover
                popupState={popupStateSysOpen}
                state={popoverState}
                onSave={handleCopyOrSave}
            />
        </Fragment>
    );
}

Table.propTypes = {
    loading: PropTypes.bool.isRequired
};

export default Table;
